// Chemin: C:\gestion-copro\src\utils\imageCompressor.ts
// Utilitaire de compression d'images
// Redimensionne et compresse les photos avant upload vers Firebase Storage

/**
 * Options de compression
 */
interface CompressionOptions {
  maxWidth?: number;      // Largeur max en pixels (défaut: 1200)
  maxHeight?: number;     // Hauteur max en pixels (défaut: 1200)
  quality?: number;       // Qualité JPEG 0-1 (défaut: 0.8)
  mimeType?: string;      // Type MIME de sortie (défaut: image/jpeg)
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.8,
  mimeType: 'image/jpeg'
};

/**
 * Compresse une image en la redimensionnant et en réduisant sa qualité
 * @param file - Fichier image original
 * @param options - Options de compression
 * @returns Promise<File> - Fichier compressé
 */
export const compressImage = (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    // Vérifier que c'est bien une image
    if (!file.type.startsWith('image/')) {
      reject(new Error('Le fichier n\'est pas une image'));
      return;
    }

    // Créer un lecteur de fichier
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions en conservant le ratio
        let { width, height } = img;
        const maxW = opts.maxWidth!;
        const maxH = opts.maxHeight!;

        if (width > maxW || height > maxH) {
          const ratio = Math.min(maxW / width, maxH / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Créer un canvas pour redimensionner
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'));
          return;
        }

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en blob compressé
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Échec de la compression'));
              return;
            }

            // Générer un nouveau nom de fichier
            const originalName = file.name.replace(/\.[^.]+$/, '');
            const extension = opts.mimeType === 'image/jpeg' ? 'jpg' : 'png';
            const newFileName = `${originalName}_compressed.${extension}`;

            // Créer un nouveau File à partir du blob
            const compressedFile = new File([blob], newFileName, {
              type: opts.mimeType,
              lastModified: Date.now()
            });

            // Log pour debug (à retirer en production)
            const savings = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
            console.log(
              `📷 Compression: ${file.name}`,
              `| ${(file.size / 1024).toFixed(0)} KB → ${(compressedFile.size / 1024).toFixed(0)} KB`,
              `| -${savings}%`,
              `| ${width}x${height}px`
            );

            resolve(compressedFile);
          },
          opts.mimeType,
          opts.quality
        );
      };

      img.onerror = () => {
        reject(new Error('Impossible de charger l\'image'));
      };

      img.src = event.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Impossible de lire le fichier'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Compresse plusieurs images
 * @param files - Tableau de fichiers images
 * @param options - Options de compression
 * @returns Promise<File[]> - Tableau de fichiers compressés
 */
export const compressImages = async (
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> => {
  const compressionPromises = files.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
};

/**
 * Vérifie si une image a besoin d'être compressée
 * @param file - Fichier image
 * @param maxSizeKB - Taille max en KB (défaut: 500)
 * @returns boolean
 */
export const needsCompression = (file: File, maxSizeKB: number = 500): boolean => {
  return file.size > maxSizeKB * 1024;
};
